// ============================================================
// ClientUsageTab.js
// Usage tab inside <ClientDetailPage>. Aggregates UsageLog for
// one Client over a date window.
//
// Backend reference:
//   backend/func-carehub-api/src/functions/admin-clients-usage.js
//
// The backend currently supports groupBy='day' only — each row
// is one (date, moduleKey) pair. The Usage tab therefore renders
// a single table with Date / Module / Invocations / Users /
// Tokens columns. When backend grouping by module or by user
// lands, surface a groupBy selector and switch render mode.
//
// IMPORTANT: UsageLog is empty in production today — agent
// endpoints don't yet write to it (CLAUDE.md Gotcha #28). The
// empty state is normal; the operator-facing copy reflects that.
// If this tab starts returning rows, agent-side audit has begun
// landing — worth flagging during ops review.
//
// URL state:
//   ?startDate=YYYY-MM-DD ?endDate=YYYY-MM-DD
// Auto-refetch on change (no Apply button). End-before-start is
// validated inline before hitting the network.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle, RotateCw } from 'lucide-react';
import { getAdminClientUsage } from '../api/adminClients';
import { translateError } from '../utils/errorMessages';
import StatusMessage from '../components/StatusMessage';
import './ClientUsageTab.css';

const DEFAULT_WINDOW_DAYS = 30;

function toIsoDate(d) {
  // YYYY-MM-DD in UTC. Native <input type="date"> emits this
  // format directly; we use it consistently in URL params too.
  return d.toISOString().substring(0, 10);
}

function defaultStartDate() {
  const d = new Date(Date.now() - DEFAULT_WINDOW_DAYS * 86400000);
  return toIsoDate(d);
}

function defaultEndDate() {
  return toIsoDate(new Date());
}

function isValidIsoDate(s) {
  if (typeof s !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime());
}

export default function ClientUsageTab({ clientId }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const urlStart = searchParams.get('startDate');
  const urlEnd   = searchParams.get('endDate');

  const startDate = isValidIsoDate(urlStart) ? urlStart : defaultStartDate();
  const endDate   = isValidIsoDate(urlEnd)   ? urlEnd   : defaultEndDate();

  const rangeInvalid = startDate && endDate && endDate < startDate;

  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ invocations: 0, tokens: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (rangeInvalid) return; // inline error handles it
    setLoading(true);
    setError(null);
    try {
      const res = await getAdminClientUsage(clientId, {
        startDate: `${startDate}T00:00:00Z`,
        endDate:   `${endDate}T23:59:59Z`,
        groupBy:   'day',
      });
      setItems(res.items || []);
      setTotals({
        invocations: res.totalInvocations || 0,
        tokens:      res.totalTokens || 0,
      });
    } catch (err) {
      setError(translateError(err));
    } finally {
      setLoading(false);
    }
  }, [clientId, startDate, endDate, rangeInvalid]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function handleDateChange(key, value) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value); else next.delete(key);
      return next;
    });
  }

  return (
    <div className="client-usage">
      <div className="client-usage__filters">
        <label className="client-usage__field">
          <span className="client-usage__label">From</span>
          <input
            type="date"
            value={startDate}
            onChange={(e) => handleDateChange('startDate', e.target.value)}
            className="client-usage__input"
          />
        </label>
        <label className="client-usage__field">
          <span className="client-usage__label">To</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => handleDateChange('endDate', e.target.value)}
            className="client-usage__input"
          />
        </label>
      </div>

      {rangeInvalid && (
        <div className="client-usage__inline-error" role="alert">
          End date must be on or after the start date.
        </div>
      )}

      {!rangeInvalid && loading && (
        <StatusMessage variant="loading" className="client-usage__state">
          <Loader2 size={16} className="client-usage__spinner" />
          <span>Loading usage…</span>
        </StatusMessage>
      )}

      {!rangeInvalid && !loading && error && (
        <StatusMessage variant="error" className="client-usage__state client-usage__state--error">
          <AlertCircle size={16} />
          <span>{error}</span>
          <button type="button" className="client-usage__retry" onClick={fetchData}>
            <RotateCw size={14} />
            Retry
          </button>
        </StatusMessage>
      )}

      {!rangeInvalid && !loading && !error && items.length === 0 && (
        <StatusMessage variant="empty" className="client-usage__state client-usage__state--empty">
          <p>
            Activity tracking will appear here once agent usage is recorded.
            This is normal for newly created Clients.
          </p>
        </StatusMessage>
      )}

      {!rangeInvalid && !loading && !error && items.length > 0 && (
        <>
          <div className="client-usage__totals">
            <span><strong>{totals.invocations.toLocaleString()}</strong> invocations</span>
            <span><strong>{totals.tokens.toLocaleString()}</strong> tokens</span>
          </div>
          <table className="client-usage__table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Module</th>
                <th style={{ textAlign: 'right' }}>Invocations</th>
                <th style={{ textAlign: 'right' }}>Users</th>
                <th style={{ textAlign: 'right' }}>Tokens</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row, i) => (
                <tr key={`${row.date}-${row.moduleKey}-${i}`}>
                  <td>{row.date}</td>
                  <td className="client-usage__module">{row.moduleKey}</td>
                  <td style={{ textAlign: 'right' }}>{row.invocationCount}</td>
                  <td style={{ textAlign: 'right' }}>{row.userCount}</td>
                  <td style={{ textAlign: 'right' }}>{row.tokensUsed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
